/**
 * 播放器运行环境。
 */
export type VideoPlaybackRuntimeEnv = 'development' | 'staging' | 'production'

/**
 * 播放器功能配置。
 */
export type VideoPlaybackFeatureConfig = {
    /** 当前运行环境。 */
    env: VideoPlaybackRuntimeEnv
    /** 是否展示播放诊断入口。 */
    enableDebugEntry: boolean
    /** 是否允许展示播放诊断面板。 */
    enableDebugPanel: boolean
    /** 是否允许手动切换播放源。 */
    enableManualSourceSwitch: boolean
    /** 是否展示播放源角标。 */
    enableSourceBadge: boolean
    /** 是否输出详细遥测日志。 */
    enableVerboseTelemetry: boolean
    /** 是否允许派发远端遥测。 */
    enableRemoteTelemetryReporter: boolean
}

/**
 * 播放提示文案。
 */
export type VideoPlaybackUiText = {
    /** 通用加载文案。 */
    initialLoading: string
    /** HLS 初始化文案。 */
    hlsLoading: string
    /** 高清加载文案。 */
    hdLoading: string
    /** 低清加载文案。 */
    lowQualityLoading: string
    /** 切换播放源文案。 */
    switchingSource: string
    /** 自动降级提示文案。 */
    autoDowngrade: string
    /** 回退到兜底源提示文案。 */
    fallback: string
    /** 手动重试 HLS 提示文案。 */
    retryHls: string
    /** 手动切回高清提示文案。 */
    switchBackHd: string
    /** 播放中异常时提示用户手动处理。 */
    manualSwitchRequired: string
}

/**
 * 解析当前播放器所处环境。
 *
 * @returns 当前环境标识
 */
function resolveRuntimeEnv(): VideoPlaybackRuntimeEnv {
    if (import.meta.env.DEV) {
        return 'development'
    }

    const mode = String(import.meta.env.MODE || '').toLowerCase()
    if (mode.includes('test') || mode.includes('staging') || mode.includes('sit') || mode.includes('uat')) {
        return 'staging'
    }
    return 'production'
}

/** 当前播放器环境。 */
export const videoPlaybackRuntimeEnv = resolveRuntimeEnv()

/** 当前播放器功能开关。 */
export const videoPlaybackFeatureConfig: VideoPlaybackFeatureConfig = {
    env: videoPlaybackRuntimeEnv,
    enableDebugEntry: videoPlaybackRuntimeEnv !== 'production',
    enableDebugPanel: videoPlaybackRuntimeEnv !== 'production',
    enableManualSourceSwitch: videoPlaybackRuntimeEnv !== 'production',
    enableSourceBadge: videoPlaybackRuntimeEnv !== 'production',
    enableVerboseTelemetry: videoPlaybackRuntimeEnv !== 'production',
    enableRemoteTelemetryReporter: videoPlaybackRuntimeEnv !== 'development'
}

/**
 * 获取当前环境的播放器文案配置。
 *
 * @returns 播放文案配置
 */
export function getVideoPlaybackUiText(): VideoPlaybackUiText {
    if (videoPlaybackRuntimeEnv === 'production') {
        return {
            initialLoading: '视频加载中...',
            hlsLoading: '视频加载中...',
            hdLoading: '正在准备视频...',
            lowQualityLoading: '正在准备视频...',
            switchingSource: '正在切换播放版本...',
            autoDowngrade: '正在切换更稳定的播放版本',
            fallback: '已切换到可播放版本',
            retryHls: '正在重新加载视频...',
            switchBackHd: '正在重新加载视频...',
            manualSwitchRequired: '当前播放异常，请手动切换清晰度'
        }
    }

    return {
        initialLoading: '加载中...',
        hlsLoading: '正在初始化 HLS 视频...',
        hdLoading: '正在加载高清视频...',
        lowQualityLoading: '正在加载低清视频...',
        switchingSource: '正在切换备用视频源...',
        autoDowngrade: '当前清晰度资源加载较慢，已切换更流畅版本',
        fallback: '当前清晰度资源不可用，已切换备用视频源',
        retryHls: '正在重试流媒体播放',
        switchBackHd: '正在切回高清视频',
        manualSwitchRequired: '当前播放异常，请手动切换清晰度或重试'
    }
}

/**
 * 判断当前详情是否应优先尝试 HLS。
 *
 * @param detail 课程或培训详情
 * @returns `true` 表示允许优先尝试 m3u8
 */
export function shouldPreferHlsPlayback(detail?: Record<string, any> | null) {
    if (!detail || typeof detail !== 'object') {
        return true
    }

    const candidates = [
        detail.hlsReady,
        detail.m3u8Ready,
        detail.playSourceReady,
        detail.trainAnnex?.hlsReady,
        detail.trainAnnex?.m3u8Ready
    ]
    const explicitFlag = candidates.find((value) => typeof value === 'boolean')
    if (typeof explicitFlag === 'boolean') {
        return explicitFlag
    }

    const sourceType = String(detail.playSourceType || detail.trainAnnex?.playSourceType || '').toLowerCase()
    if (sourceType === 'mp4') {
        return false
    }
    if (sourceType === 'm3u8' || sourceType === 'hls') {
        return true
    }

    return true
}
